import * as Sentry from "@sentry/react";
import {
    atom,
    WritableAtom,
    Getter,
    Atom,
    SetStateAction,
    Setter,
    ExtractAtomArgs,
    PrimitiveAtom,
} from "jotai";
import { atomEffect } from "jotai-effect";
import Yup, { ISchema, mixed } from "yup";

import { t } from "../../i18n";
import { assertNever } from "../../obj";

import {
    FieldState,
    FormAtom,
    Option,
    ValidationActionAtom,
    ValidationResult,
} from "./types";

// In this file we use extensively generics that may be promises, which is not supported by eslint await-thenable

// Support for Prism, where baseType can be undefined when reading, but not in write direction.
export function formAtom<BaseType extends FieldType, FieldType = BaseType>(
    baseAtom: WritableAtom<
        BaseType | undefined,
        [SetStateAction<BaseType>],
        any
    >,
    schema: ISchema<BaseType>,
    parentFormAtom?: FormAtom<any>,
    areEqual?: (a: Awaited<FieldType>, b: Awaited<FieldType>) => boolean
): FormAtom<FieldType>;

/**
 * Create a new FormAtom for `baseAtom`
 *
 * # State diagram
 * ```
 * ┌─────────────┐
 * │ NOT_TOUCHED │ Initial state
 * └─────▲───────┘
 *       │
 *       │ reset
 *       │
 * ┌─────┴─────┐   setCacheValue     ┌────────┐
 * │ Any state ├─────────────────────► CACHED │
 * └─────┬─────┘                     └────────┘
 *       │
 *       │ dispatchValue
 *       │
 * ┌─────▼──────┐  Validation error  ┌─────────┐
 * │ VALIDATING ├────────────────────► ERRORED │
 * └─────┬──────┘                    └────▲────┘
 *       │                                │
 *       │ Success                        │
 *       │                                │
 * ┌─────▼───────┐ External error         │
 * │ DISPATCHING ├────────────────────────┘
 * └─────┬───────┘ (like server error, ...)
 *       │
 *       │ Success
 *       │
 * ┌─────▼──────┐
 * │ DISPATCHED │
 * └────────────┘
 * ```
 * @see FieldState's variants in types.ts in the same folder for more info on each state
 *
 * `BaseType` is the `baseAtom` underlying type
 *
 * `FieldType` is an optional less restrictive type for `BaseType`
 * for cases where the field can send invalid values filtered by the validator
 *
 * # Write behaviour
 * The `baseAtom`'s value is supposed to be valid, but we run the validator on `NOT_TOUCHED` values, which allows for:
 *  - Empty required fields on creation forms
 *  - Stricter validation is implemented after value was persisted
 *
 * # Reset behaviour
 * Remove internal state and dispatch to `baseAtom` its original value (the 'old value' on first dispatchValue call)
 * This value is **NOT** validated.
 *
 * @param `baseAtom` the atom we read value from and propagate back validated values.
 * @param `schema` the Yup validation schema
 * @param `parentFormAtom` the parent form atom. the new atom will subscribe to events on the parent one:
 *  - forwardValidation: forward a validation request (a write to `validateFieldAtom`) on parentFormAtom to this atom
 * @param `areEqual` the value comparison function used to define if the value is dirty
 */
export function formAtom<BaseType extends FieldType, FieldType = BaseType>(
    baseAtom: WritableAtom<BaseType, [SetStateAction<BaseType>], any>,
    schema: ISchema<BaseType>,
    parentFormAtom?: FormAtom<any>,
    areEqual: (a: Awaited<FieldType>, b: Awaited<FieldType>) => boolean = (
        a,
        b
    ) => a === b
): FormAtom<FieldType> {
    // A counter used to make sure we don't erase a newer state after an await point
    // This counter must be incremented on all set / dispatch calls
    // NOTE: It's OK to use a regular variable here since it is never exposed / rendered
    let currentChangeId = 0;

    // Validate valueToValidate, then dispatch the validated value to baseAtom.
    // Aborts when the user enters a new value (based on currentChangeId)
    // Triggered by dispatchValue and validate calls
    const validateThenDispatchValue = async (
        get: Getter,
        set: Setter,
        valueToValidate: Awaited<FieldType>,
        dirty: boolean
    ): Promise<ValidationResult<undefined>> => {
        // Save the change id we had when we started
        const startChangeId = currentChangeId;
        let validationSucceeded = false;
        try {
            const validationPromise = schema.validate(valueToValidate, {
                context: { get, set },
            });
            set(fieldStateAtom, {
                type: "VALIDATING",
                value: valueToValidate,
                dirty,
                validationPromise: validationPromise.then(
                    () => ({ type: "SUCCESS" as const, values: undefined }),
                    (err) => {
                        const yupError = err as Yup.ValidationError;
                        const error = {
                            yup: yupError,
                            value:
                                yupError.errors?.join(", ") ??
                                t("Validation_Invalid"),
                        };
                        return { type: "ERROR" as const, errors: [error] };
                    }
                ),
            });
            const validatedValue = await validationPromise;
            if (currentChangeId !== startChangeId) {
                console.debug(
                    `Superseded by another change (${currentChangeId} != ${startChangeId}) after validation`,
                    validatedValue
                );
                return { type: "SUPERSEDED" };
            }
            validationSucceeded = true;
            if (validatedValue !== get(baseAtom)) {
                const dispatchPromise = Promise.resolve(
                    set(baseAtom, validatedValue)
                );
                set(fieldStateAtom, {
                    type: "DISPATCHING",
                    value: validatedValue,
                    dirty,
                    dispatchPromise: dispatchPromise.then(() => {}),
                });
                await dispatchPromise;
                if (currentChangeId !== startChangeId) {
                    console.debug(
                        `Superseded by another change (${currentChangeId} != ${startChangeId}) after dispatch`,
                        validatedValue
                    );
                    return { type: "SUPERSEDED" };
                }
            }
            set(fieldStateAtom, {
                type: "DISPATCHED",
                dirty,
            });
        } catch (err: unknown) {
            const yupError = err as Yup.ValidationError;
            if (yupError.errors === undefined) {
                const sentryError = new Error(
                    "Field validation failed but got no error message. Fallback to generic error!"
                );
                console.error(sentryError);
                Sentry.captureException(sentryError);
            }
            console.error(yupError.errors ?? err);
            if (currentChangeId !== startChangeId) {
                console.debug(
                    `Superseded by another change (${currentChangeId} != ${startChangeId}) in error catch`,
                    valueToValidate
                );
                return { type: "SUPERSEDED" };
            }
            const error = {
                yup: yupError,
                value: yupError.errors?.join(", ") ?? t("Validation_Invalid"),
            };
            set(fieldStateAtom, {
                type: "ERRORED",
                dirty,
                value: valueToValidate,
                error,
                validationSucceeded,
            });
            return {
                type: "ERROR",
                errors: [error],
            };
        }
        return {
            type: "SUCCESS",
            values: undefined,
        };
    };

    // Create the internal state atom for this field, default to "NOT_TOUCHED"
    const fieldStateAtom = atom<FieldState<FieldType>>({
        type: "NOT_TOUCHED",
        dirty: false,
    });
    // Proxy all calls to fieldStateAtom, increments currentChangeId along the way
    const fieldStateAtomProxy = atom(
        (get) => get(fieldStateAtom),
        (_get, set, ...rest: ExtractAtomArgs<typeof fieldStateAtom>) => {
            currentChangeId += 1;
            return set(fieldStateAtom, ...rest);
        }
    );

    // Create the write-only atom used to force validation
    const validateFieldAtom: ValidationActionAtom = atom(
        null,
        async (
            get,
            set,
            forceRevalidation
        ): Promise<ValidationResult<undefined>> => {
            const subscriberResults = await Promise.all(
                [...subscribers.values()].map((validationActionAtom) =>
                    set(validationActionAtom, forceRevalidation)
                )
            );
            const aggregatedResult = subscriberResults.reduce(
                (acc, result) => {
                    switch (result.type) {
                        case "ERROR":
                            return {
                                type: "ERROR",
                                errors: [
                                    ...(acc.type === "ERROR" ? acc.errors : []),
                                    ...result.errors,
                                ],
                            };
                        case "SUPERSEDED":
                            return acc.type === "ERROR"
                                ? acc
                                : { type: "SUPERSEDED" };
                        case "SUCCESS":
                            return acc;
                        default:
                            return assertNever(result);
                    }
                },
                {
                    type: "SUCCESS",
                    values: undefined,
                }
            );

            if (aggregatedResult.type !== "SUCCESS") {
                return aggregatedResult;
            }

            const value = get(baseAtom);
            const fieldState = get(fieldStateAtom);

            let requireValidation: boolean;
            let existingPromise:
                | Promise<ValidationResult<undefined> | void>
                | undefined;
            let valueToValidate: Awaited<FieldType>;
            switch (fieldState.type) {
                case "CACHED":
                    console.warn(
                        "Requested to validate non-dispatched data, may indicate a missing dispatchValue call"
                    );
                    requireValidation = true;
                    valueToValidate = fieldState.value;
                    break;
                case "ERRORED":
                    requireValidation = true;
                    valueToValidate = fieldState.value;
                    break;
                case "VALIDATING":
                    requireValidation = false;
                    existingPromise = fieldState.validationPromise;
                    valueToValidate = fieldState.value;
                    break;
                case "DISPATCHING":
                    requireValidation = false;
                    existingPromise = fieldState.dispatchPromise;
                    valueToValidate = fieldState.value;
                    break;
                case "DISPATCHED":
                    requireValidation = false;
                    valueToValidate = await value;
                    break;
                case "NOT_TOUCHED":
                    requireValidation = true;
                    valueToValidate = await value;
                    break;
                default:
                    assertNever(fieldState);
            }

            if (requireValidation || forceRevalidation) {
                return validateThenDispatchValue(
                    get,
                    set,
                    valueToValidate,
                    fieldState.dirty
                );
            }

            return (
                (await existingPromise) ?? {
                    type: "SUCCESS",
                    values: undefined,
                }
            );
        }
    );
    const subscribeToParentAtom =
        parentFormAtom &&
        atomEffect(() =>
            parentFormAtom.subscribeToForwardValidation(validateFieldAtom)
        );

    // Create the atom that stores the old value on first set
    const initialValueAtom = atom({ defined: false } as Option<
        Awaited<FieldType>
    >);
    const resetFieldAtom = atom(null, async (get, set) => {
        const initialValue = get(initialValueAtom);

        if (initialValue.defined) {
            // Temporary store the initial value, invalidating current changes if needed
            set(fieldStateAtomProxy, {
                type: "CACHED",
                dirty: false,
                value: initialValue.value,
            });
            set(initialValueAtom, { defined: false });
            const myChangeId = currentChangeId;
            await set(baseAtom, initialValue.value as Awaited<BaseType>);
            if (currentChangeId === myChangeId) {
                // Set back the state to "NOT_TOUCHED" if no modification has occurred since parent set
                set(fieldStateAtom, {
                    type: "NOT_TOUCHED",
                    dirty: false,
                });
            }
        } else {
            set(fieldStateAtomProxy, {
                type: "NOT_TOUCHED",
                dirty: false,
            });
        }
    });

    const getFieldDisplayValue = (get: Getter): FieldType => {
        if (subscribeToParentAtom) {
            get(subscribeToParentAtom);
        }
        const fieldState = get(fieldStateAtom);
        switch (fieldState.type) {
            case "DISPATCHED":
            case "NOT_TOUCHED":
                return get(baseAtom);
            case "CACHED":
            case "VALIDATING":
            case "DISPATCHING":
            case "ERRORED":
                return fieldState.value;
        }
    };

    // This atom exposes the base functionality of formAtom:
    // The value read from it is the value we should display
    // The value written to it is validated and dispatched to baseAtom
    const derivedAtom = atom(
        getFieldDisplayValue,
        async (get, set, update: SetStateAction<FieldType>) => {
            // We will write a new value to fieldStateAtom, so we need to bump currentChangeId
            const startChangeId = (currentChangeId += 1);

            const nonAwaitedNewValue =
                update instanceof Function
                    ? update(getFieldDisplayValue(get))
                    : update;

            if (!(nonAwaitedNewValue instanceof Promise)) {
                // Set the cache value immediately, before crossing any await point,
                // to give immediate feedback (set(myFormAtom, foo); get(myFormAtom) === foo)
                set(fieldStateAtom, ({ dirty }) => ({
                    type: "CACHED",
                    value: nonAwaitedNewValue as Awaited<FieldType>,
                    dirty,
                }));
            }

            // Persist initial value for reset
            const initialValue = get(initialValueAtom);
            let awaitedInitialValue;
            if (initialValue.defined) {
                awaitedInitialValue = initialValue.value;
            } else {
                awaitedInitialValue = await get(baseAtom);
                set(initialValueAtom, {
                    defined: true,
                    value: awaitedInitialValue,
                });
            }

            const newValue = await nonAwaitedNewValue;
            if (currentChangeId !== startChangeId) {
                console.debug(
                    `Superseded by another change (${currentChangeId} != ${startChangeId}) after value computation`,
                    newValue
                );
                return;
            }
            await validateThenDispatchValue(
                get,
                set,
                newValue,
                !areEqual(awaitedInitialValue, newValue)
            );
        }
    );

    const subscribers = new Set<ValidationActionAtom>();
    const subscribeToForwardValidation = (atom: ValidationActionAtom) => {
        subscribers.add(atom);
        return () => {
            subscribers.delete(atom);
        };
    };

    return {
        ...derivedAtom,
        fieldStateAtom: fieldStateAtomProxy,
        validateFieldAtom,
        subscribeToForwardValidation,
        resetFieldAtom,
        initialValueAtom,
        schema,
        areEqual,
    };
}

/**
 * Create a FormAtom that throw exceptions when updated
 *
 * You should prefer creating a variant field for read-only that takes an `Atom<FieldType>` directly
 *
 * @param baseAtom
 */
export function readOnlyFormAtom<FieldType>(
    baseAtom: Atom<FieldType>
): FormAtom<FieldType> {
    const derivedAtom = atom(
        (get) => get(baseAtom),
        (_get, _set, update: SetStateAction<FieldType>): Promise<void> => {
            throw new Error(
                `Cannot update read-only form atom ${baseAtom} (payload: ${update})`
            );
        }
    );

    const initialValue: FieldState<FieldType> = {
        type: "NOT_TOUCHED",
        dirty: false,
    };
    const fieldStateAtom: PrimitiveAtom<FieldState<FieldType>> = atom(
        () => initialValue,
        (_get, _set, update): void => {
            throw new Error(
                `Cannot update field state in read-only form atom ${baseAtom} (payload: ${JSON.stringify(
                    update
                )})`
            );
        }
    );

    const validateFieldAtom = atom(null, () => {
        return Promise.resolve<ValidationResult<undefined>>({
            type: "SUCCESS",
            values: undefined,
        });
    });
    const subscribeToForwardValidation = () => {
        return () => {};
    };
    const resetFieldAtom = atom(null, () => {
        return Promise.resolve();
    });

    return {
        ...derivedAtom,
        fieldStateAtom,
        initialValueAtom: atom({ defined: false } as Option<
            Awaited<FieldType>
        >),
        validateFieldAtom,
        subscribeToForwardValidation,
        resetFieldAtom,
        schema: mixed<any>(),
        // This function is never called since the value never changes and initialValueAtom is never defined.
        areEqual: () => false,
    };
}
